import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { clearInterpreterIframeUrl } from "slices/interpreter/interpreterIframeSlice";
import { getEcho } from "services/echo";
import { X, AlertTriangle } from "lucide-react";

const JoinInterpreterVideoCall = () => {
  const { id } = useParams();
  const { state: routeState } = useLocation();
  const iframeUrl = useSelector((state) => state.interpreterIframe.interpreterIframeUrl);
  const dispatch = useDispatch();
  const [sessionEnded, setSessionEnded] = useState(false);
  const [takenByOther, setTakenByOther] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const isReplacement = routeState?.isReplacement === true;
  const showBanner = isReplacement && !bannerDismissed;

  // Listen on appointment channel for both session-ended and interpreter-replaced events.
  // Using the appointment channel avoids interfering with IncomingCallPopup's listeners
  // on the interpreter's own channel.
  useEffect(() => {
    if (!id) return;
    const echo = getEcho();
    const channelName = `appointment.${id}`;
    const channel = echo.channel(channelName);
    channel
      .listen(".session-ended", () => {
        dispatch(clearInterpreterIframeUrl());
        setSessionEnded(true);
      })
      .listen(".interpreter-replaced", () => {
        dispatch(clearInterpreterIframeUrl());
        setTakenByOther(true);
      });
    return () => {
      channel.stopListening(".session-ended");
      channel.stopListening(".interpreter-replaced");
      echo.leave(channelName);
    };
  }, [id, dispatch]);

  return (
    <div
      className="content"
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
      }}
    >
      {/* Replacement disclaimer banner — stacked above the iframe */}
      {showBanner && (
        <div
          style={{
            background: "#fff3cd",
            borderBottom: "1px solid #ffc107",
            padding: "12px 20px",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={20} color="#856404" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <strong style={{ display: "block", color: "#856404", marginBottom: 2, fontSize: 14 }}>
              You are joining as a replacement interpreter
            </strong>
            <span style={{ fontSize: 13, color: "#533f03", lineHeight: 1.5 }}>
              The previous interpreter was removed from this session. Please introduce yourself
              to the client and apologize for any interruption before continuing the interpretation.
            </span>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              flexShrink: 0,
              color: "#856404",
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Video area fills all remaining height */}
      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {iframeUrl && !sessionEnded && !takenByOther ? (
          <iframe
            title="100ms-app"
            allow="camera *;microphone *;display-capture *"
            src={iframeUrl}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        ) : (
          <div
            className="container-fluid d-flex flex-column align-items-center justify-content-center bg-light p-0"
            style={{ height: "100%" }}
          >
            <p>
              {takenByOther
                ? "This appointment has been assigned to another interpreter."
                : sessionEnded
                ? "The session has been ended by the host."
                : "No video call in progress."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinInterpreterVideoCall;
